/**
 * Meta Graph API Error Parsing (Story 9.2, Task 1.7)
 *
 * Shared helper for all Meta adapters (Stories 9.2-9.5).
 * Parses Meta's JSON error body to extract error.code and maps it to AdapterErrorCode.
 *
 * Meta returns auth/permission errors as HTTP 400 with structured JSON body:
 * { error: { message: "...", type: "OAuthException", code: 190, error_subcode: 463 } }
 *
 * safeFetch() maps HTTP 400 → "invalid_response" by default — this parser overrides
 * with the correct semantic AdapterErrorCode.
 */

import type { AdapterErrorCode } from "../adapter";

// ============================================================================
// Types
// ============================================================================

interface MetaGraphErrorBody {
  error?: {
    message?: string;
    type?: string;
    code?: number;
    error_subcode?: number;
    fbtrace_id?: string;
  };
}

export interface ParsedMetaError {
  code: AdapterErrorCode;
  message: string;
  retryable: boolean;
  /**
   * Story 35.5 — Meta error sub-code, exposed to callers that need to
   * branch on specific failure shapes (e.g. the v26 subcode 2655 self-heal
   * flow in `facebook-ads.ts` that updates the attribution_setting KV
   * cache and retries the insights request once). `undefined` when the
   * upstream error did not include a subcode.
   */
  errorSubcode?: number;
}

/**
 * Story 35.5 — Meta Graph API v26 error subcode emitted when an insights
 * request specifies `action_attribution_windows` against an ad account
 * whose `attribution_setting` is `"use_account_default"`.
 *
 * Source: developers.facebook.com/docs/graph-api/changelog/version26.0
 * verified 2026-05-19.
 *
 * Adapter self-heals by updating the attribution_setting KV cache to
 * `"use_account_default"` and retrying once without
 * `action_attribution_windows` (and with `use_account_attribution_setting=true`).
 */
export const META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE = 2655;

// ============================================================================
// Error Code Mapping
// ============================================================================

/**
 * Maps Meta Graph API error.code values to AdapterErrorCode.
 *
 * Reference: https://developers.facebook.com/docs/graph-api/guides/error-handling/
 *  - 190: token_expired (OAuthException — expired or invalidated token)
 *  - 200: permission_denied (requires permission to access this endpoint)
 *  - 4, 10, 17, 32: rate_limited (too many calls / too much data / too many API calls)
 *  - 803: account_not_found (object does not exist or has been deleted)
 */
const META_ERROR_CODE_MAP: Record<number, AdapterErrorCode> = {
  190: "token_expired",
  200: "permission_denied",
  4: "rate_limited",
  10: "rate_limited",
  17: "rate_limited",
  32: "rate_limited",
  803: "account_not_found",
};

const RETRYABLE_CODES: ReadonlySet<AdapterErrorCode> = new Set([
  "rate_limited",
  "provider_unavailable",
  "network_error",
]);

// ============================================================================
// Parser
// ============================================================================

/**
 * Parses a Meta Graph API error response body and returns the mapped AdapterErrorCode.
 *
 * If the body cannot be parsed or doesn't contain a recognized error.code,
 * returns null — the caller should fall back to safeFetch()'s default mapping.
 *
 * @param responseBody - The raw response body text from a Meta API error response
 * @returns ParsedMetaError if a known Meta error code is found, null otherwise
 */
export function parseMetaGraphError(responseBody: string): ParsedMetaError | null {
  let parsed: MetaGraphErrorBody;
  try {
    parsed = JSON.parse(responseBody) as MetaGraphErrorBody;
  } catch {
    return null;
  }

  const errorCode = parsed.error?.code;
  if (errorCode === undefined || errorCode === null) {
    return null;
  }

  const errorSubcode = typeof parsed.error?.error_subcode === "number" ? parsed.error.error_subcode : undefined;

  // Story 35.5 — v26 subcode 2655 ("attribution-settings mismatch").
  // Mapped to `invalid_request` (retryable=false at the safeFetch layer)
  // so the adapter's self-heal flow has time to detect the subcode,
  // update the KV cache, and retry once with corrected query params.
  if (errorSubcode === META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE) {
    return {
      code: "invalid_request",
      message: parsed.error?.message ?? `Meta API error subcode ${META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE}`,
      retryable: false,
      errorSubcode,
    };
  }

  const adapterCode = META_ERROR_CODE_MAP[errorCode];
  if (!adapterCode) {
    return null;
  }

  const message = parsed.error?.message ?? `Meta API error code ${errorCode}`;

  return {
    code: adapterCode,
    message,
    retryable: RETRYABLE_CODES.has(adapterCode),
    errorSubcode,
  };
}
